'use client';

import { useState } from 'react';

import { gql } from '@apollo/client';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from '@apollo/client/react';
import { useSetAtom } from 'jotai';
import { usePostHog } from 'posthog-js/react';
import { useTranslations } from 'use-intl';

import {
  captureResponseCompletedOnce,
  captureResponseStartedOnce,
  useIsFunnelTracked,
} from '@/shared-measurement';
import { isMatrixAnswerText } from '@/shared-project-insights';
import {
  ActivityStore,
  AUDIO_CAPTURE_BUFFER_MS,
  clearAudioQueueAtom,
  getPanelIdAndProvider,
  useProjectResponseAudioRecord,
  useProjectResponseModeContext,
  type ResponderPanel,
} from '@/shared-survey';
import {
  ADD_RECORDING_EVENT,
  AddMessageV3Input,
  Channel,
  FORMAT_TRANSCRIPT_ANSWER,
  FormatTranscriptAnswerQueryVariables,
  GetProjectQuery,
  InitiateSurveyV3Mutation,
  InsightRecordingEventType,
  MessageSender,
  MessageV2,
  ProjectMode,
  ProjectType,
  QuestionType,
  V2_GET_INSIGHT,
  V2GetInsightQuery,
  V3_ADD_INSIGHT,
  V3_ADD_MESSAGE,
} from '@/survey-graphql';
import { toast } from '@/ui';

import {
  OPTIMISTIC_MESSAGE_TEMP_REPLY_ID,
  V2_OPTIMISTIC_REPLY_MESSAGE,
  V2_OPTIMISTIC_SENT_MESSAGE,
} from './project-response-chat.helpers';
import type { ProjectResponseDeliveryGate } from './project-response-delivery-gate.types';
import {
  createErrorHandler,
  emitNonPrimaryRecordingEvents,
  handleIframeUrl,
  handleRecordingEvent,
  handleRedirectActions,
  handleVoiceModeCompletion,
  normalizeAsset,
  sortMessagesByCreatedAt,
} from './project-response-handler.helpers';
import { isUrlQuestion } from './project-response-preview.hooks';
import { useProjectResponseTTS } from './project-response-tts.hooks';
import { NIL_UUID } from './project-response.consts';
import { enumStringEquals, v2UpsertMessage } from './project-response.helpers';
import { resolveLegacyUrlQuestionTranscript } from './survey-project-response-url-question-text.helpers';

interface UseProjectResponseHandlerProps {
  speechAudiosData: ReturnType<typeof useProjectResponseTTS>;
  insightData: V2GetInsightQuery | undefined;
  initialMessageFromProject: MessageV2;
  panel: ResponderPanel | null;
  redirectUrlRef: React.MutableRefObject<string | null>;
  conversationOverRef: React.MutableRefObject<string | null>;
  redirectActionRef: React.MutableRefObject<
    'standard' | 'new-window' | 'new-tab'
  >;
  setInsightId: React.Dispatch<React.SetStateAction<string | null>>;
  project: GetProjectQuery['project'];
  responderEmail: string | undefined;
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  sessionId: string;
  channel: Channel | undefined;
  setIframeUrl: (url: string) => void;
  setMobileAppEmbedUrl: (url: string | null) => void;
  getCurrentRecordingTime: () => number;
  setIsPublished: React.Dispatch<React.SetStateAction<boolean>>;
  insightId: string | null;
  activityStore: React.MutableRefObject<ActivityStore>;
  /** When set, AI moderated (UsabilityTesting) uses this instead of toast for "We could not get your answer" */
  onTranscriptAnswerError?: () => void;
  onSurveyComplete?: () => void;
  onRuntimeRedirectSet?: () => void;
  deliveryGate?: ProjectResponseDeliveryGate;
}

export function useProjectResponseHandler({
  speechAudiosData,
  insightData,
  initialMessageFromProject,
  panel,
  redirectUrlRef,
  conversationOverRef,
  redirectActionRef,
  setInsightId,
  project,
  responderEmail,
  firstQuestion,
  sessionId,
  channel,
  setIframeUrl,
  setMobileAppEmbedUrl,
  getCurrentRecordingTime,
  setIsPublished,
  insightId,
  activityStore,
  onTranscriptAnswerError,
  onSurveyComplete,
  onRuntimeRedirectSet,
  deliveryGate,
}: UseProjectResponseHandlerProps) {
  const posthog = usePostHog();
  const isFunnelTracked = useIsFunnelTracked();

  const [isSaving, setSaving] = useState(false);
  const [replyFeedbackIds, setReplyFeedbackIds] = useState<Set<string>>(
    () => new Set()
  );

  const { replaceMessageId } = speechAudiosData;
  function getSpeech(id: string, text: string, shouldInterrupt = true) {
    const play = () => speechAudiosData.getSpeech(id, text, shouldInterrupt);
    return deliveryGate ? deliveryGate.deliverSpeech(id, play) : play();
  }
  async function getSpeechForMessages(
    messages: Array<{ id: string; text: string; transcript?: string }>
  ) {
    if (!deliveryGate) return speechAudiosData.getSpeechForMessages(messages);
    for (const [index, message] of messages.entries()) {
      const text = message.transcript || message.text;
      if (text) await getSpeech(message.id, text, index === 0);
    }
  }
  const client = useApolloClient();
  const [addInsight, { loading: addInsightLoading }] =
    useMutation(V3_ADD_INSIGHT);
  const [addMessage, { loading: addMessageLoading }] =
    useMutation(V3_ADD_MESSAGE);
  const [formatTranscriptAnswer] = useLazyQuery(FORMAT_TRANSCRIPT_ANSWER, {
    errorPolicy: 'all',
  });
  const [addRecordingEvent] = useMutation(ADD_RECORDING_EVENT);
  const t = useTranslations('Usability');

  const {
    reset: resetAudioRecord,
    createMessageAsset: createMessageAudioAsset,
    lock: lockAudioRecord,
    unlock: unlockAudioRecord,
  } = useProjectResponseAudioRecord(
    project.organization?.id,
    project?.anonymous
  );

  const {
    projectMode,
    setCurrentTranscript,
    responseMode,
    startListening,
    startDateOfTranscript,
    lastDateOfTranscript,
    currentTranscript,
    stopListening,
  } = useProjectResponseModeContext();

  const handleCreateInsight = async (
    firstQuestionAnswer: string,
    transcript: string | null,
    assets: AddMessageV3Input['assets'] | undefined
  ) => {
    setSaving(true);
    const timestamp = Date.now();
    const initialMetadata = initialMessageFromProject.metadata;
    // Capture the voice-utterance start for the first answer up front; used
    // below to anchor its UserVoiceResponse event to where the user began
    // speaking rather than the post-STT commit moment.
    const startDate = startDateOfTranscript();

    await addInsight({
      optimisticResponse: {
        addInsightV3: {
          id: NIL_UUID,
          messages: [
            {
              ...initialMessageFromProject,
              createdAt: new Date().toISOString(),
              transcript: initialMessageFromProject.transcript ?? null,
              contentUrl: initialMessageFromProject.contentUrl ?? null,
              mobileAppEmbedUrl:
                initialMessageFromProject.mobileAppEmbedUrl ?? null,
              mobileAppFileName:
                initialMessageFromProject.mobileAppFileName ?? null,
              optionRenderType:
                initialMessageFromProject.optionRenderType ?? null,
              assets:
                initialMessageFromProject.assets?.map(normalizeAsset) ?? null,
              metadata: {
                ...initialMetadata,
                type: initialMetadata?.type ?? QuestionType.OpenEnded,
                options: (initialMetadata?.options ?? []).map((option) => ({
                  label: option.label,
                  value: option.value,
                  assets: option.assets?.map(normalizeAsset) ?? null,
                })),
                isConversationOver:
                  initialMetadata?.isConversationOver ?? false,
                assistantQuestion: initialMetadata?.assistantQuestion ?? false,
                questionId:
                  initialMetadata?.questionId ?? firstQuestion.id ?? null,
                required: initialMetadata?.required ?? true,
                maxSelections: initialMetadata?.maxSelections ?? null,
              },
            },
            {
              ...V2_OPTIMISTIC_SENT_MESSAGE,
              text: firstQuestionAnswer,
              transcript,
              createdAt: new Date(timestamp + 1000).toISOString(),
            },
            {
              ...V2_OPTIMISTIC_REPLY_MESSAGE,
              createdAt: new Date(timestamp + 2000).toISOString(),
            },
          ],
          contact: null,
          progress: 0.1,
          actions: null,
        },
      },
      update: (cache, { data, errors }) => {
        if (!data?.addInsightV3) {
          return;
        }
        setInsightId(data?.addInsightV3.id);
        const { actions, ...rest } = data.addInsightV3;
        const orderedMessages = sortMessagesByCreatedAt(
          data.addInsightV3.messages ?? []
        );
        if (!errors && data.addInsightV3.id !== NIL_UUID) {
          deliveryGate?.acceptMessages(orderedMessages);
        }

        const firstMessage = orderedMessages[0];
        if (
          firstMessage &&
          firstMessage.id &&
          typeof firstMessage.id === 'string'
        ) {
          replaceMessageId(NIL_UUID, firstMessage.id);
        }
        const lastMessage = orderedMessages?.[orderedMessages.length - 1];
        if (deliveryGate) {
          if (!errors && data.addInsightV3.id !== NIL_UUID) {
            const answerIndex = orderedMessages.findIndex(
              (message) => message.sender === MessageSender.User
            );
            if (answerIndex !== -1) {
              void getSpeechForMessages(
                orderedMessages
                  .slice(answerIndex + 1)
                  .filter(
                    (message) => message.sender === MessageSender.Assistant
                  )
                  .map((message) => ({
                    id: message.id,
                    text: message.text,
                    transcript:
                      resolveLegacyUrlQuestionTranscript(
                        message.text,
                        message.transcript,
                        project.language
                      ) ?? undefined,
                  }))
              );
            }
          }
        } else if (
          lastMessage &&
          lastMessage.id &&
          typeof lastMessage.id === 'string' &&
          lastMessage.id !== OPTIMISTIC_MESSAGE_TEMP_REPLY_ID
        ) {
          void getSpeech(
            lastMessage.id,
            resolveLegacyUrlQuestionTranscript(
              lastMessage.text,
              lastMessage.transcript,
              project.language
            ) ??
              lastMessage.text ??
              ''
          );
        }
        const insightV2 = { ...rest, __typename: 'InsightV2' as const };
        cache.writeQuery({
          query: V2_GET_INSIGHT,
          variables: { id: data.addInsightV3.id },
          data: { insightV2 },
        });
        const isConversationOver =
          data.addInsightV3.messages?.some(
            (m) => m.metadata?.isConversationOver
          ) ?? false;
        handleRedirectActions({
          actions,
          panel,
          redirectUrlRef,
          conversationOverRef,
          redirectActionRef,
          isConversationOver,
          onRuntimeRedirectSet,
        });
        if (isConversationOver) {
          if (isFunnelTracked && data.addInsightV3.id !== NIL_UUID) {
            const target = {
              projectId: project?.id,
              responseId: data.addInsightV3.id,
            };
            // `update` runs before `onCompleted`; start first so funnels order.
            captureResponseStartedOnce(posthog, target);
            captureResponseCompletedOnce(posthog, target);
          }
          onSurveyComplete?.();
        }
      },
      onCompleted: async ({ addInsightV3 }) => {
        if (isFunnelTracked) {
          captureResponseStartedOnce(posthog, {
            projectId: project?.id,
            responseId: addInsightV3.id,
          });
        }
        setInsightId(addInsightV3.id);
        setSaving(false);
        resetAudioRecord();
        window.parent.postMessage(
          {
            type: 'insight-added',
          },
          '*'
        );

        const firstQuestionMessage = addInsightV3.messages?.find(
          (m) =>
            m.sender === MessageSender.Assistant &&
            m.metadata?.questionId === firstQuestion.id
        );
        const reply = addInsightV3.messages?.find(
          (m) =>
            m.sender === MessageSender.Assistant &&
            m.metadata?.questionId !== firstQuestion.id
        );

        if (!isUrlQuestion(firstQuestion) && firstQuestionMessage?.id) {
          await handleRecordingEvent({
            projectType: project.type ?? ProjectType.Survey,
            insightId: addInsightV3.id,
            messageId: firstQuestionMessage.id,
            time: 0,
            type: InsightRecordingEventType.PrimaryQuestion,
            addRecordingEvent,
          });
        }

        handleIframeUrl({
          reply,
          setIframeUrl,
          setMobileAppEmbedUrl,
        });

        if (
          reply &&
          reply.id &&
          project.type === ProjectType.UsabilityTesting &&
          reply.metadata?.assistantQuestion !== true &&
          reply.metadata?.isConversationOver !== true &&
          reply.contentUrl === null
        ) {
          const currentTime = getCurrentRecordingTime();
          if (currentTime > 0 || isUrlQuestion(firstQuestion)) {
            await handleRecordingEvent({
              projectType: project.type ?? ProjectType.Survey,
              insightId: addInsightV3.id,
              messageId: reply.id,
              time: currentTime,
              type: InsightRecordingEventType.PrimaryQuestion,
              addRecordingEvent,
            });
          }
        }

        // Emit non-primary recording events (AIFollowUp / UserVoiceResponse
        // / UserTypedResponse) for messages in this batch. Primary-question
        // events are produced by the `handleRecordingEvent` calls above; the
        // classifier returns null for primaries, so this only emits the new
        // kinds. Anchor the voice answer to its utterance start (see
        // handleAddMessage for the same derivation).
        const voiceResponseTime =
          startDate != null
            ? Math.max(
                0,
                Math.round(
                  getCurrentRecordingTime() - (Date.now() - startDate) / 1000
                )
              )
            : null;
        await emitNonPrimaryRecordingEvents({
          projectType: project.type ?? ProjectType.Survey,
          insightId: addInsightV3.id,
          messages: addInsightV3.messages ?? [],
          getCurrentRecordingTime,
          addRecordingEvent,
          voiceResponseTime,
        });

        handleVoiceModeCompletion({
          messages: addInsightV3.messages ?? [],
          responseMode,
          setCurrentTranscript,
          startListening: () => {
            const last = addInsightV3.messages?.findLast(
              (message) => message.sender === MessageSender.Assistant
            );
            if (deliveryGate && last) {
              void deliveryGate.deliverMessageEffect(last.id, async () => {
                await startListening();
              });
            } else void startListening();
          },
        });
      },
      onError: (error) => {
        createErrorHandler({
          error,
          setIsPublished,
          setSaving,
        });
      },
      variables: {
        input: {
          projectId: project.id,
          email: responderEmail,
          questionId: firstQuestion.id,
          userResponse: firstQuestionAnswer,
          channel,
          sessionId: sessionId,
          userTranscript: transcript,
          assets: assets,
          ...getPanelIdAndProvider(panel),
        },
      },
    });
  };

  const clearAudioQueue = useSetAtom(clearAudioQueueAtom);

  const handleAddMessage = async (
    input: Pick<AddMessageV3Input, 'content'> & { transcript?: string }
  ) => {
    // Clear any existing audio queue when user submits a new message
    // This prevents old questions from playing if voice mode is toggled later
    clearAudioQueue();

    const startDate = startDateOfTranscript();
    const lastDate = lastDateOfTranscript();
    const lastContentUpdateAt = activityStore.current.lastContentUpdateAt;
    const shouldUseVoiceTranscript =
      !lastContentUpdateAt || (lastDate && lastDate > lastContentUpdateAt);

    let content = input.content;
    let transcript: string | null = input.transcript ?? null;
    const messages = [
      ...(insightData?.insightV2.messages ?? [initialMessageFromProject]),
    ];
    const sortedMessages = sortMessagesByCreatedAt(messages, false);
    const lastAssistantMessage = sortedMessages.find(
      (m) => m.sender === MessageSender.Assistant
    );
    const lastMetadata = lastAssistantMessage?.metadata;
    let shouldLockAndUnlock = false;
    const isStructuredOptionQuestion =
      lastMetadata?.type &&
      lastMetadata.type !== QuestionType.OpenEnded &&
      lastMetadata.type !== QuestionType.Task &&
      lastMetadata.type !== QuestionType.Message;
    const isClickedOptionAnswer =
      isMatrixAnswerText(input.content) ||
      Boolean(
        input.content?.trim() &&
        enumStringEquals(projectMode, ProjectMode.HandsFree) &&
        isStructuredOptionQuestion
      );
    if (isClickedOptionAnswer) {
      transcript = null;
    } else if (responseMode === 'voice' && shouldUseVoiceTranscript) {
      const lastQuestionType = lastMetadata?.type;

      const transcriptInput: FormatTranscriptAnswerQueryVariables['input'] = {
        questionType: lastQuestionType ?? QuestionType.OpenEnded,
        options: lastMetadata?.options?.map((opt) => ({
          value: opt.value,
          label: opt.label,
        })),
        transcript: transcript ?? currentTranscript,
      };

      const cachedOutput = client.cache.readQuery({
        query: FORMAT_TRANSCRIPT_ANSWER,
        variables: {
          input: transcriptInput,
        },
      });

      shouldLockAndUnlock = !cachedOutput;

      if (shouldLockAndUnlock) {
        lockAudioRecord();
        await stopListening();
      }

      const { data } = await formatTranscriptAnswer({
        variables: {
          input: transcriptInput,
        },
      });

      const formattedTranscript =
        data?.formattedTranscriptAnswer?.formattedTranscript;

      if (
        lastQuestionType &&
        lastQuestionType !== QuestionType.OpenEnded &&
        lastQuestionType !== QuestionType.Task &&
        lastQuestionType !== QuestionType.Message &&
        currentTranscript !== ''
      ) {
        if (
          data?.formattedTranscriptAnswer.extractedOptions &&
          data?.formattedTranscriptAnswer.extractedOptions.length
        ) {
          const extractedOptions =
            data.formattedTranscriptAnswer.extractedOptions;
          content = extractedOptions.join(',') ?? formattedTranscript;
          if (lastQuestionType === QuestionType.MultipleChoice) {
            const maxSelections = lastMetadata?.maxSelections;
            if (
              maxSelections &&
              (!extractedOptions || extractedOptions.length > maxSelections)
            ) {
              toast.add({
                title: t('optionsMaxSelectionError', {
                  count: maxSelections,
                }),
                type: 'error',
              });
              if (shouldLockAndUnlock) {
                void startListening();
              }
              return;
            }
          } else if (
            lastQuestionType === QuestionType.Ranking &&
            extractedOptions
          ) {
            const allOptions = lastMetadata?.options;
            const missingOptions = allOptions
              ?.filter((o) =>
                extractedOptions.every((e) => e.trim() !== o.value)
              )
              .map((o) => o.value)
              .join(',');
            content += missingOptions ? ',' + missingOptions : '';
          }
        } else {
          if (
            project.type === ProjectType.UsabilityTesting &&
            onTranscriptAnswerError
          ) {
            onTranscriptAnswerError();
          } else {
            toast.add({
              title: t('transcriptErrorBody'),
              type: 'error',
            });
          }
          setTimeout(() => {
            if (shouldLockAndUnlock) {
              void startListening();
            }
          }, 500);
          return;
        }
      } else {
        content = input.transcript ?? currentTranscript;
      }
      if (formattedTranscript) {
        transcript = formattedTranscript;
      }
      transcript ??= currentTranscript;
    }
    let audioAsset = null;
    if (!enumStringEquals(projectMode, ProjectMode.HandsFree)) {
      audioAsset = await createMessageAudioAsset(
        startDate ? startDate - AUDIO_CAPTURE_BUFFER_MS : undefined,
        false
      );
    }
    const assets = audioAsset ? [audioAsset] : undefined;
    if (!insightData || insightData.insightV2.id === NIL_UUID) {
      await handleCreateInsight(content, transcript, assets);
      if (shouldLockAndUnlock) {
        unlockAudioRecord();
      }
      return;
    } else {
      const timestamp = lastAssistantMessage?.createdAt
        ? new Date(lastAssistantMessage.createdAt).getTime()
        : Date.now();

      await addMessage({
        onCompleted: async ({ addMessageV3 }) => {
          const newMessages = [addMessageV3.sent, addMessageV3.reply];

          handleVoiceModeCompletion({
            messages: newMessages,
            responseMode,
            setCurrentTranscript,
            unlockAudioRecord,
            resetAudioRecord,
            startListening: () => {
              if (deliveryGate && addMessageV3.reply) {
                void deliveryGate.deliverMessageEffect(
                  addMessageV3.reply.id,
                  async () => {
                    await startListening();
                  }
                );
              } else void startListening();
            },
            shouldLockAndUnlock,
          });

          const reply = addMessageV3.reply;

          handleIframeUrl({
            reply,
            setIframeUrl,
            setMobileAppEmbedUrl,
          });

          if (
            reply &&
            reply.id &&
            project.type === ProjectType.UsabilityTesting &&
            reply.metadata?.assistantQuestion !== true &&
            reply.metadata?.isConversationOver !== true &&
            reply.contentUrl === null
          ) {
            const currentTime = getCurrentRecordingTime();
            if (currentTime > 0 && insightId) {
              await handleRecordingEvent({
                projectType: project.type ?? ProjectType.Survey,
                insightId: insightId,
                messageId: reply.id,
                time: currentTime,
                type: InsightRecordingEventType.PrimaryQuestion,
                addRecordingEvent,
              });
            }
          }

          // Emit non-primary events (AIFollowUp / UserVoiceResponse /
          // UserTypedResponse) for the sent + reply pair. Primaries are
          // skipped by the classifier and remain handled by the
          // `handleRecordingEvent` call above.
          if (insightId) {
            // Anchor the voice answer to where the user STARTED speaking
            // (startDate, captured at function entry) rather than commit
            // time: recording-time-now minus wall-clock elapsed since the
            // utterance began. Null falls back to the commit-time clock.
            const voiceResponseTime =
              startDate != null
                ? Math.max(
                    0,
                    Math.round(
                      getCurrentRecordingTime() -
                        (Date.now() - startDate) / 1000
                    )
                  )
                : null;
            await emitNonPrimaryRecordingEvents({
              projectType: project.type ?? ProjectType.Survey,
              insightId,
              // Include `replyFeedback` (e.g. the closing thank-you message,
              // which is an assistant follow-up) so the final entry still gets
              // a timestamp. On the last turn `reply` is null; the emit skips
              // nullish entries.
              messages: [
                addMessageV3.sent,
                addMessageV3.replyFeedback,
                addMessageV3.reply,
              ],
              getCurrentRecordingTime,
              addRecordingEvent,
              voiceResponseTime,
            });
          }
        },
        onError: (error) => {
          createErrorHandler({
            error,
            setIsPublished,
            setCurrentTranscript,
            unlockAudioRecord,
            responseMode,
            startListening,
            shouldLockAndUnlock,
          });
        },
        optimisticResponse: {
          addMessageV3: {
            sent: {
              ...V2_OPTIMISTIC_SENT_MESSAGE,
              text: content ?? '',
              transcript,
              createdAt: new Date(timestamp + 1000).toISOString(),
            },
            reply: {
              ...V2_OPTIMISTIC_REPLY_MESSAGE,
              createdAt: new Date(timestamp + 2000).toISOString(),
            },
            replyFeedback: null,
            surveyProgress: null,
            actions: null,
          },
        },
        update: (cache, { data, errors }) => {
          if (errors) {
            cache.writeQuery({
              query: V2_GET_INSIGHT,
              variables: { id: insightId ?? '' },
              data: {
                insightV2: {
                  ...insightData?.insightV2,
                  id: insightData?.insightV2.id,
                  messages: (insightData?.insightV2.messages ?? []).filter(
                    (m) => !m.metadata?.isConversationOver
                  ),
                },
              },
            });
          }
          if (!data?.addMessageV3) {
            return;
          }
          let messages = insightData?.insightV2?.messages ?? [];
          for (const message of [
            data.addMessageV3.sent,
            data.addMessageV3.replyFeedback,
            data.addMessageV3.reply,
          ]) {
            if (message) messages = v2UpsertMessage(messages, message);
          }
          if (
            !errors &&
            data.addMessageV3.sent.id !== V2_OPTIMISTIC_SENT_MESSAGE.id
          ) {
            deliveryGate?.acceptMessages(sortMessagesByCreatedAt(messages));
          }

          // Collect all AI messages (replyFeedback and reply) for TTS
          const aiMessages = [];
          if (
            data.addMessageV3.replyFeedback &&
            data.addMessageV3.replyFeedback.id !==
              OPTIMISTIC_MESSAGE_TEMP_REPLY_ID
          ) {
            aiMessages.push({
              id: data.addMessageV3.replyFeedback.id,
              text: data.addMessageV3.replyFeedback.text,
              transcript:
                resolveLegacyUrlQuestionTranscript(
                  data.addMessageV3.replyFeedback.text,
                  data.addMessageV3.replyFeedback.transcript,
                  project.language
                ) ?? undefined,
            });
            // Track this ID so downstream consumers can identify replyFeedback
            // messages without relying on fragile positional heuristics.
            setReplyFeedbackIds((prev) =>
              new Set(prev).add(data.addMessageV3.replyFeedback?.id || '')
            );
          }
          if (
            data.addMessageV3.reply &&
            data.addMessageV3.reply.id !== OPTIMISTIC_MESSAGE_TEMP_REPLY_ID
          ) {
            aiMessages.push({
              id: data.addMessageV3.reply.id,
              text: data.addMessageV3.reply.text,
              transcript:
                resolveLegacyUrlQuestionTranscript(
                  data.addMessageV3.reply.text,
                  data.addMessageV3.reply.transcript,
                  project.language
                ) ?? undefined,
            });
          }

          // Generate speech for all AI messages in sequence
          if (
            aiMessages.length > 0 &&
            (!deliveryGate ||
              (!errors &&
                data.addMessageV3.sent.id !== V2_OPTIMISTIC_SENT_MESSAGE.id))
          ) {
            void getSpeechForMessages(aiMessages);
          }

          const currentProgress =
            cache.readQuery<V2GetInsightQuery>({
              query: V2_GET_INSIGHT,
              variables: { id: insightId },
            })?.insightV2.progress ?? 0;
          cache.writeFragment({
            id: `InsightV2:${insightId}`,
            data: {
              messages,
              progress:
                data.addMessageV3.surveyProgress &&
                data.addMessageV3.surveyProgress > currentProgress
                  ? data.addMessageV3.surveyProgress
                  : currentProgress,
              variables: { id: insightId },
            },
            fragment: gql`
              fragment NewMessages on InsightV2 {
                messages
                progress
              }
            `,
          });
          const isConversationOver =
            data.addMessageV3.reply?.metadata?.isConversationOver ?? false;
          handleRedirectActions({
            actions: data.addMessageV3.actions,
            panel,
            redirectUrlRef,
            conversationOverRef,
            redirectActionRef,
            isConversationOver,
            onRuntimeRedirectSet,
          });
          if (isConversationOver) {
            if (isFunnelTracked) {
              captureResponseCompletedOnce(posthog, {
                projectId: project?.id,
                responseId: insightId,
              });
            }
            onSurveyComplete?.();
          }
        },
        variables: {
          input: {
            content,
            insightId: insightId ?? '',
            sessionId,
            transcript,
            assets,
          },
        },
      });
    }
  };

  return {
    handleCreateInsight,
    isSaving,
    addInsightLoading,
    addMessageLoading,
    handleAddMessage,
    replyFeedbackIds,
    resetAudioRecord,
  };
}
